import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Table, Button, Input, Space, Tag, Card, Typography, notification, Select, Badge, Modal } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";
import { SearchOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { IGallery } from "../../services/nestjs-base-api/types";
import { useGetAllGalleriesQuery, useGetGalleryDetailsQuery } from "../../services/nestjs-base-api/admin-api";
import { GalleryTypeEnum } from "../../enums/GalleryTypeEnum";
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title } = Typography;
const { Search } = Input;

const AdminGalleriesPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [eventFilter, setEventFilter] = useState<number | null>(null);
  const [expandedGalleryId, setExpandedGalleryId] = useState<number | null>(null);
  
  // Fetch galleries data
  const { data, error, isLoading } = useGetAllGalleriesQuery({
    jwtToken: user.jwtToken,
  }, { pollingInterval: 600000 });

  // Fetch gallery details when expanded
  const { data: galleryDetails } = useGetGalleryDetailsQuery(
    { jwtToken: user.jwtToken, galleryId: expandedGalleryId || 0 },
    { skip: !expandedGalleryId }
  );

  useEffect(() => {
    if (error) {
      if ('status' in error) {
        if (error.status === 401) {
          logout();
        }
      }
    }
  }, [error, logout]);

  const galleries = data || [];

  // Filter galleries based on search, type, and event
  const filteredGalleries = galleries.filter((gallery: IGallery) => {
    // Filter by search text
    const matchesSearch = searchText ? 
      gallery.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (gallery.description && gallery.description.toLowerCase().includes(searchText.toLowerCase())) :
      true;
    
    // Filter by gallery type
    const matchesType = typeFilter ? 
      gallery.type === typeFilter :
      true;
    
    // Filter by event
    const matchesEvent = eventFilter ? 
      gallery.eventId === eventFilter :
      true;
    
    return matchesSearch && matchesType && matchesEvent;
  });

  // Handle gallery view
  const handleViewGallery = (id: number) => {
    navigate(`/admin/galleries/${id}`);
  };

  // Handle gallery deletion with confirmation
  const handleDeleteGallery = (id: number, name: string) => {
    Modal.confirm({
      title: intl.formatMessage(
        { id: 'gallery.delete.confirm.title' },
        { name }
      ) || `Delete Gallery: ${name}`,
      content: (
        <>
          <p>
            {intl.formatMessage({ id: 'gallery.delete.confirm.content' }) ||
              'This will permanently delete the gallery and all its photos. This action cannot be undone.'}
          </p>
          <p style={{ fontWeight: 'bold' }}>"{name}"</p>
        </>
      ),
      okText: intl.formatMessage({ id: 'common.delete' }) || 'Delete',
      cancelText: intl.formatMessage({ id: 'common.cancel' }) || 'Cancel',
      okButtonProps: { danger: true },
      onOk: () => {
        // In a real implementation, this would call the API to delete the gallery
        notification.success({
          message: intl.formatMessage({ id: 'admin.galleries.deleteSuccess' }) || 'Gallery Deleted',
          description: intl.formatMessage(
            { id: 'admin.galleries.deleteSuccessDesc' },
            { name }
          ) || `Gallery "${name}" has been successfully deleted.`,
        });
      },
    });
  };

  // Get unique events for filter
  const events = Array.from(new Set(galleries.map((gallery) => gallery.eventId)));

  // Function to get gallery type display name
  const getGalleryTypeName = (type: string): string => {
    switch (type) {
      case GalleryTypeEnum.Event:
        return intl.formatMessage({ id: 'gallery.type.event' }) || 'Event';
      case GalleryTypeEnum.Reference:
        return intl.formatMessage({ id: 'gallery.type.reference' }) || 'Reference';
      default:
        return type;
    }
  };

  // Function to get gallery type color
  const getGalleryTypeColor = (type: string): string => {
    switch (type) {
      case GalleryTypeEnum.Event:
        return 'blue';
      case GalleryTypeEnum.Reference:
        return 'purple';
      default:
        return 'default';
    }
  };

  // Expanded row render with gallery details
  const expandedRowRender = (record: IGallery) => {
    const photoCount = galleryDetails?.photoCount || 0;
    
    return (
      <div style={{ padding: '20px' }}>
        <Title level={5}>Gallery Details</Title>
        
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div><strong>Gallery ID:</strong> {record.id}</div>
          <div><strong>Hash:</strong> {record.hash}</div>
          <div><strong>Created At:</strong> {dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss')}</div>
          <div><strong>Updated At:</strong> {dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm:ss')}</div>
          <div>
            <strong>Description:</strong> {record.description || 'No description provided'}
          </div>
          <div>
            <Badge count={photoCount} showZero>
              <span style={{ padding: '0 8px' }}><strong>Photos:</strong></span>
            </Badge>
          </div>
        </Space>
      </div>
    );
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IGallery, b: IGallery) => a.id - b.id,
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: IGallery, b: IGallery) => a.name.localeCompare(b.name),
    },
    {
      title: 'Event',
      dataIndex: 'event.id',
      key: 'eventId',
      sorter: (a: IGallery, b: IGallery) => a.event.name.localeCompare(b.event.name),
      render: (_: any, record: IGallery) => record.event.name,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={getGalleryTypeColor(type)}>
          {getGalleryTypeName(type)}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a: IGallery, b: IGallery) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: IGallery) => (
        <Space size="small">
          <Button 
            icon={<EyeOutlined />} 
            onClick={() => handleViewGallery(record.id)} 
            title="View"
          />
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteGallery(record.id, record.name)} 
            title="Delete"
          />
        </Space>
      ),
    },
  ];

  return (
    <Content style={{ margin: '0 16px' }}>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.galleriesName'} defaultMessage="Galleries" />
        </Breadcrumb.Item>
      </Breadcrumb>
      
      <Card
        title={
          <Title level={2}>
            <FormattedMessage id={'admin.galleries.title'} defaultMessage="Manage Galleries" />
          </Title>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <Search
              placeholder={intl.formatMessage({ id: 'admin.galleries.searchPlaceholder' }) || "Search galleries by name or description"}
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={value => setSearchText(value)}
              style={{ width: 300 }}
            />
            
            <Space>
              <Select
                placeholder={intl.formatMessage({ id: 'admin.galleries.typeFilter' }) || "Filter by type"}
                style={{ width: 150 }}
                allowClear
                onChange={(value) => setTypeFilter(value)}
              >
                <Select.Option value={GalleryTypeEnum.Event}>Event</Select.Option>
                <Select.Option value={GalleryTypeEnum.Reference}>Reference</Select.Option>
              </Select>
              
              <Select
                placeholder={intl.formatMessage({ id: 'admin.galleries.eventFilter' }) || "Filter by event"}
                style={{ width: 180 }}
                allowClear
                onChange={(value) => setEventFilter(value)}
              >
                {events.map((eventId: number) => (
                  <Select.Option key={eventId} value={eventId}>
                    Event {eventId}
                  </Select.Option>
                ))}
              </Select>
            </Space>
          </div>
          
          <Table 
            dataSource={filteredGalleries} 
            columns={columns} 
            rowKey="id"
            loading={isLoading}
            pagination={{ pageSize: 10 }}
            expandable={{
              expandedRowRender,
              onExpand: (expanded, record) => {
                if (expanded) {
                  setExpandedGalleryId(record.id);
                } else {
                  setExpandedGalleryId(null);
                }
              }
            }}
          />
        </Space>
      </Card>
    </Content>
  );
};

export default AdminGalleriesPage;